import React from 'react';
import { Link, useForm } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import Avatar from '@/components/common/Avatar';
import InputError from '@/components/Forms/InputError';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import useRoutes from '@/hooks/useRoutes';
import { ArrowLeft, ExternalLink } from 'lucide-react';

interface SubmissionData {
    id: number;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string;
    reviewed_at: string | null;
    assignment: {
        id: number;
        title: string;
        course: { id: number; title: string };
        section: { id: number; title: string } | null;
        topic: { id: number; title: string } | null;
    };
    student: {
        id: number;
        full_name: string;
        email: string;
        avatar_url: string | null;
    };
    reviewed_by: {
        id: number;
        full_name: string;
    } | null;
    teacher_comment: string;
    url_1: string | null;
    url_2: string | null;
    evidence_url: string | null;
    assignment_labels: {
        url_1_label: string;
        url_2_label: string;
        evidence_label: string;
    };
}

interface SubmissionEditProps {
    submission: SubmissionData;
    errors?: Record<string, string>;
}

const EditContent: React.FC<SubmissionEditProps> = ({ submission, errors: serverErrors }) => {
    const { route } = useRoutes();

    const { data, setData, post, processing, errors } = useForm({
        status: submission.status,
        teacher_comment: submission.teacher_comment || '',
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        post(
            route('staff-assignment-submissions-edit', {
                submission_id: submission.id,
            }).toString()
        );
    };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-3xl">
                {/* Header */}
                <div className="mb-6 flex items-center gap-4">
                    <Link
                        href={route('staff-assignment-submissions-detail', {
                            submission_id: submission.id,
                        }).toString()}
                        className="rounded-md p-2 text-gray-600 hover:bg-gray-100"
                    >
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            Calificar entrega
                        </h1>
                        <p className="mt-1 text-gray-600">{submission.assignment.title}</p>
                    </div>
                </div>

                {/* Student Info Card */}
                <div className="mb-6 rounded-lg bg-white p-6 shadow">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">
                        Estudiante
                    </h2>
                    <div className="flex items-center gap-4">
                        <Avatar user={submission.student} size="lg" />
                        <div>
                            <div className="text-lg font-medium text-gray-900">
                                {submission.student.full_name}
                            </div>
                            <div className="text-sm text-gray-500">
                                {submission.student.email}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Submission Info Card (Read-only) */}
                <div className="mb-6 rounded-lg bg-white p-6 shadow">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">
                        Información de la entrega
                    </h2>

                    <div className="space-y-4">
                        {submission.url_1 ? (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">
                                    {submission.assignment_labels.url_1_label}
                                </label>
                                <a
                                    href={submission.url_1}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
                                >
                                    {submission.url_1}
                                    <ExternalLink size={16} />
                                </a>
                            </div>
                        ) : null}

                        {submission.url_2 ? (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">
                                    {submission.assignment_labels.url_2_label}
                                </label>
                                <a
                                    href={submission.url_2}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
                                >
                                    {submission.url_2}
                                    <ExternalLink size={16} />
                                </a>
                            </div>
                        ) : null}

                        {submission.evidence_url && (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">
                                    {submission.assignment_labels.evidence_label}
                                </label>
                                <a
                                    href={submission.evidence_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        src={submission.evidence_url}
                                        alt="Evidencia"
                                        className="mt-2 max-w-md rounded-lg border border-gray-200"
                                    />
                                </a>
                            </div>
                        )}
                    </div>
                </div>

                {/* Edit Form */}
                <form onSubmit={handleSubmit} className="rounded-lg bg-white p-6 shadow">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">
                        Revisión de la entrega
                    </h2>

                    {(errors.general || serverErrors?.general) && (
                        <div className="mb-4 rounded-md bg-red-50 p-4">
                            <p className="text-sm text-red-800">
                                {errors.general || serverErrors?.general}
                            </p>
                        </div>
                    )}

                    <div className="space-y-6">
                        {/* Status */}
                        <div>
                            <label
                                htmlFor="status"
                                className="mb-1 block text-sm font-medium text-gray-700"
                            >
                                Estado <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="status"
                                name="status"
                                value={data.status}
                                onChange={(e) => setData('status', e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            >
                                <option value="pending">Pendiente</option>
                                <option value="approved">Aprobado</option>
                                <option value="rejected">No aprobado</option>
                            </select>
                            <InputError error={errors.status || serverErrors?.status} />
                        </div>

                        {/* Teacher Comment */}
                        <div>
                            <label
                                htmlFor="teacher_comment"
                                className="mb-1 block text-sm font-medium text-gray-700"
                            >
                                Comentarios del profesor
                            </label>
                            <textarea
                                id="teacher_comment"
                                name="teacher_comment"
                                value={data.teacher_comment}
                                onChange={(e) => setData('teacher_comment', e.target.value)}
                                rows={4}
                                placeholder="Ingresa tus comentarios sobre la entrega..."
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            />
                            <InputError error={errors.teacher_comment || serverErrors?.teacher_comment} />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3">
                            <SecondaryButton
                                as="link"
                                href={route('staff-assignment-submissions-detail', {
                                    submission_id: submission.id,
                                }).toString()}
                            >
                                Cancelar
                            </SecondaryButton>
                            <PrimaryButton
                                type="submit"
                                disabled={processing}
                                loading={processing}
                            >
                                Guardar cambios
                            </PrimaryButton>
                        </div>
                    </div>
                </form>
            </div>
        </StaffLayout>
    );
};

const Edit: React.FC<SubmissionEditProps> = (props) => {
    return (
        <ThemeWrapper>
            <EditContent {...props} />
        </ThemeWrapper>
    );
};

export default Edit;
